import { ClusterProvider, UserActionTypes, useUser } from '@myosh/myosh-login';
import { type ImperativePanelGroupHandle, Loader, ResizablePanel, ResizablePanelGroup } from '@myosh/odin-components';
import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { ThemeProvider } from '../../../context/theme.context';
import type { DBClient } from '../../../db/db-types';
import { initDB } from '../../../db/odin-db-client';
import type { OdinDBSchema } from '../../../db/odin-db-types';
import { isOfflineEnabled } from '../../../db/odin-db-util';
import { useLoginQueryParams } from '../../../hooks/use-login-query-params';
import { useAppDispatch } from '../../../redux/hooks';
import { hideActiveRecordsPanel, setActivePanelEmpty, showActiveRecordsPanel } from '../../../redux/slices/layout';
import { useActiveRecordLayout } from '../../active-record/hooks/use-active-record-layout';
import ActiveSidePanel from '../components/active-side-panel.component';
import OdinHeader from '../components/odin-header.component';
import { SchemaConfirmationModal } from '../components/schema-confirmation-modal.component';
import { shouldDisplaySchemaConfirmationModal } from '../layout-util';
import Layout from '../layout.component';
import UserLocaleInitializer from './user-locale-initializer.component';

export default function PrivateLayoutRoute() {
  const { schema } = useParams();
  const { idp, schema: idpLoginSchema, token } = useLoginQueryParams();
  const { pathname } = useLocation();
  const { state, dispatch: userDispatch } = useUser();
  const { auth: isAuthenticated, loginStatus } = state;
  const currentSchema = state.user?.currentSchema;
  const username = state.user?.preferred_username;
  const dispatch = useAppDispatch();
  const { shouldHideMainLayout, setIsFullScreen } = useActiveRecordLayout();

  const dbRef = useRef<DBClient<OdinDBSchema> | undefined>(undefined);
  const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);

  const hasTokenAndSchema = token && idpLoginSchema;
  const isLoginPage = pathname.includes('/login');
  const isSchemaConfirmationModalVisible = useMemo(
    () => shouldDisplaySchemaConfirmationModal(schema, currentSchema),
    [schema, currentSchema]
  );

  useEffect(() => {
    dispatch(showActiveRecordsPanel());
    return () => {
      dispatch(hideActiveRecordsPanel());
      dispatch(setActivePanelEmpty());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated && hasTokenAndSchema) {
      userDispatch({
        type: UserActionTypes.UpdateLoginJwt,
        payload: {
          idp,
          schema: idpLoginSchema,
          token,
        },
      });
    }
  }, []);

  if (isAuthenticated && loginStatus === 'done') {
    if (isSchemaConfirmationModalVisible && !isLoginPage) {
      return <SchemaConfirmationModal schema={schema} visible={true} />;
    }

    if (!dbRef.current && isOfflineEnabled) {
      dbRef.current = initDB({ userId: username!, schema: currentSchema! });
      if (!dbRef.current.isOpen()) {
        dbRef.current.open();
      }
    }

    return (
      <ClusterProvider>
        <UserLocaleInitializer>
          <ThemeProvider>
            <div className="grid h-dvh w-full grid-rows-[auto_1fr] overflow-hidden">
              <OdinHeader />
              <div className="row-start-2 flex h-full overflow-hidden">
                <ResizablePanelGroup ref={panelGroupRef} autoSaveId="private-route" direction="horizontal">
                  <ResizablePanel id="main-layout" order={1} className={clsx({ hidden: shouldHideMainLayout })}>
                    <Layout />
                  </ResizablePanel>
                  <ActiveSidePanel onFullScreenChanged={setIsFullScreen} />
                </ResizablePanelGroup>
              </div>
            </div>
          </ThemeProvider>
        </UserLocaleInitializer>
      </ClusterProvider>
    );
  }

  if (!isAuthenticated && !isLoginPage && !hasTokenAndSchema) {
    localStorage.setItem('postLogInRedirect', pathname);
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="mx-auto flex h-screen w-full flex-col items-center justify-center">
      <Loader />
    </div>
  );
}
